import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import axios from "axios"; // kept only for the external boardroom.sttar.in call below — do not use for internal API calls
import api from "../../api";
import { Pie, Bar } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaChartPie, FaListUl, FaClipboardList, FaRupeeSign, FaCalendarDay, FaExclamationTriangle, FaSpinner, FaHourglassHalf, FaCheckCircle } from "react-icons/fa";
import { Button, Form, Badge, Spinner, InputGroup } from "react-bootstrap";
import TechnicianPerformancePanel from "./TechnicianPerformancePanel";
import TechnicianSatisfactionPanel from "./TechnicianSatisfactionPanel";
import NewJoinerDeploymentSection from "./NewJoinerDeploymentSection";
import { getTatInfo, truncate } from "../tasks/tatUtils";
import { PriorityPill, StatusPill } from "../tasks/StatusChips";
import { taskDisplayTitle, taskDescriptionText } from "../../utils/taskDisplay";
import "../tasks/PortalHomeCards.css";
import "./ProductUnitWisePage.css";
import "./TechnicianDashboard.css";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const STATUS_ORDER = ["pending","in_progress","assigned","open","completed","closed","cancelled","rejected"];
// Semantic tone per status -- mirrors StatusChips.jsx's statusPillClass
// convention (Completed=green, In Progress/Assigned=blue, Pending=amber,
// Cancelled/Rejected=red) so charts never disagree with the table badges
// about what a status "means" visually.
const KPI_STATUS_TONE = {
  completed: "success", closed: "success", resolved: "success", done: "success",
  in_progress: "info", assigned: "info", open: "info", working: "info",
  pending: "warning",
  cancelled: "danger", rejected: "danger", failed: "danger",
};
const TONE_HEX = { success: "#16a34a", info: "#2563eb", warning: "#d97706", danger: "#dc2626", neutral: "#94a3b8" };
const colorForStatusKey = (key) => TONE_HEX[KPI_STATUS_TONE[key] || "neutral"];
const TONE_BADGE_CLASS = { success: "td-badge-green", info: "td-badge-blue", warning: "td-badge-amber", danger: "td-badge-red" };
const getBadgeClass = (status) => `td-badge ${TONE_BADGE_CLASS[KPI_STATUS_TONE[normalizeStatus(status)]] || ""}`.trim();
const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const BOARDROOM_MEETING_FILE_API = "https://boardroom.sttar.in/api/meeting-file-details";

const normalizeStatus = (v) => String(v||"unknown").trim().toLowerCase().replace(/\s+/g,"_");
const titleCase = (v) => String(v||"-").replace(/_/g," ").replace(/\w\S*/g,(t)=>t.charAt(0).toUpperCase()+t.slice(1).toLowerCase());
const safeNumber = (v) => { const n=Number(v); return Number.isFinite(n)?n:0; };
const formatMoney = (v) => safeNumber(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (v) => { if(!v) return "-"; try { return new Date(v).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); } catch { return v; } };
const isDoneStatus = (s) => s === "completed" || s === "closed";
const startOfDay = (v) => { const d = new Date(v); d.setHours(0,0,0,0); return d; };
const endOfDay = (v) => { const d = new Date(v); d.setHours(23,59,59,999); return d; };
const isSameDay = (a, ref) => {
  if (!a) return false;
  const d = new Date(a);
  if (Number.isNaN(d.getTime())) return false;
  return startOfDay(d).getTime() === startOfDay(ref).getTime();
};
const isOverdue = (task, now) => {
  if (!task.due_at) return false;
  const s = normalizeStatus(task.status);
  if (isDoneStatus(s)) return false;
  const due = new Date(task.due_at);
  return !Number.isNaN(due.getTime()) && due < now;
};
// meeting_time is a free-text range like "11:00 AM - 12:00 PM" -- extracts
// just the start time and combines it with meeting_date so two meetings on
// the same day sort (and get tagged "Next") by actual start time, not by
// date alone.
const meetingStartTimestamp = (f) => {
  const d = f.meeting_date ? new Date(f.meeting_date) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const m = String(f.meeting_time||"").match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (m) {
    let hour = parseInt(m[1],10) % 12;
    if (/PM/i.test(m[3])) hour += 12;
    d.setHours(hour, parseInt(m[2],10), 0, 0);
  } else {
    d.setHours(0,0,0,0);
  }
  return d.getTime();
};

// Bar-end value labels -- no chartjs-plugin-datalabels dependency in this
// project, so this is a tiny inline plugin (vanilla Chart.js API) scoped
// only to the chart instance it's passed into via the `plugins` prop.
const barValuePlugin = {
  id: "barValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, di) => {
      const meta = chart.getDatasetMeta(di);
      meta.data.forEach((bar, i) => {
        const value = dataset.data[i];
        ctx.save();
        ctx.font = "700 11px Inter, sans-serif";
        ctx.fillStyle = "#334155";
        ctx.textBaseline = "middle";
        ctx.textAlign = "left";
        ctx.fillText(String(value), bar.x + 6, bar.y);
        ctx.restore();
      });
    });
  },
};

function periodRange(period, customFrom, customTo, now) {
  if (period === "today") return [startOfDay(now), endOfDay(now)];
  if (period === "week") {
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    return [startOfDay(monday), endOfDay(now)];
  }
  if (period === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return [startOfDay(first), endOfDay(now)];
  }
  if (period === "custom") {
    return [
      customFrom ? startOfDay(new Date(customFrom)) : null,
      customTo ? endOfDay(new Date(customTo)) : null,
    ];
  }
  return [null, null]; // "all"
}

const PERIOD_OPTIONS = [["all","All"],["today","Today"],["week","This Week"],["month","This Month"],["custom","Custom"]];
const MY_TASK_TABS = [["all","All"],["due_today","Due Today"],["in_progress","In Progress"],["overdue","Overdue"],["completed","Completed"]];
const MY_PAGE_SIZE = 8;

const TechnicianDashboard = () => {
  const { user } = useContext(AuthContext) || {};
  const technicianId = user?.id;
  const navigate = useNavigate();
  const myTasksRef = useRef(null);
  const scheduleRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [productSummary, setProductSummary] = useState([]);
  const [allocationSummary, setAllocationSummary] = useState([]);
  // Data-driven, per-caller visibility from GET /technician-dashboard/
  // module-visibility -- the SAME permission + real-allocation rule
  // controllers/sidebarController.js applies to the matching sidebar links
  // (see that endpoint's controller). Starts false/false so neither card
  // (nor the org-wide summary fetches they depend on) ever flashes before
  // this resolves, and stays false on a failed/unauthenticated fetch --
  // fail closed, never fail open.
  const [moduleVisibility, setModuleVisibility] = useState({ products: false, allocations: false });
  const [meetingFiles, setMeetingFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Global filter bar (section 4) -- narrows the browsing/exploration
  // widgets below (My Tasks, Category Breakdown, Status Distribution,
  // Task Status chart). The 6 KPI cards and Attention Required stay
  // anchored to the real, unfiltered "today" regardless of this bar --
  // "Assigned Today" shouldn't silently become "Assigned This Month" just
  // because the user is browsing a wider window elsewhere on the page.
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterCustomFrom, setFilterCustomFrom] = useState("");
  const [filterCustomTo, setFilterCustomTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterPriority, setFilterPriority] = useState("ALL");
  const [filterCategory, setFilterCategory] = useState("ALL");
  const [filterUnit, setFilterUnit] = useState("ALL");

  // My Tasks (section 8) -- its own tab/search/sort/pagination, separate
  // from the global filter bar above.
  const [myTab, setMyTab] = useState("all");
  const [myTasksSearch, setMyTasksSearch] = useState("");
  const [mySort, setMySort] = useState("latest");
  const [myPage, setMyPage] = useState(1);

  const [meetingsExpanded, setMeetingsExpanded] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if(technicianId) fetchDashboardData(); }, [technicianId]);
  useEffect(() => { setMyPage(1); }, [myTab, myTasksSearch, mySort, filterPeriod, filterCustomFrom, filterCustomTo, filterStatus, filterPriority, filterCategory, filterUnit]);

  const fetchDashboardData = async () => {
    if (!technicianId) return;
    setLoading(true);

    // Resolved FIRST (and separately) so the two org-wide unit summary
    // endpoints below are simply never called when this caller has no
    // legitimate Product/Allocation visibility -- not just hidden in the
    // UI. Fails closed (both false) on any error, same as the state's own
    // initial value.
    let visibility = { products: false, allocations: false };
    try {
      const visRes = await api.get(`/technician-dashboard/module-visibility`);
      visibility = visRes.data?.moduleVisibility || visibility;
    } catch (e) {
      // fail closed -- cards/fetches stay off
    }
    setModuleVisibility(visibility);

    // Each of these 4 widgets is independent -- one failing (a transient
    // network blip, a session expiring mid-request) must not blank out the
    // other 3, which is what a single Promise.all + shared catch used to do
    // (any one rejection left tasks/categories/productSummary/
    // allocationSummary all stuck at their empty initial state).
    const [taskRes, categoryRes, productSummaryRes, allocationSummaryRes] = await Promise.allSettled([
      api.get(`/technician-tasks/${technicianId}`),
      api.get(`/task-categories-breakdown/${technicianId}`),
      visibility.products ? api.get(`/products/unit-wise/summary`) : Promise.resolve({ data: [] }),
      visibility.allocations ? api.get(`/alloc/unit-wise/summary`) : Promise.resolve({ data: [] }),
    ]);

    if (taskRes.status === "fulfilled") {
      const cleanedTasks = Array.isArray(taskRes.value.data) ? taskRes.value.data.map((t)=>({...t,status:normalizeStatus(t.status)})) : [];
      setTasks(cleanedTasks);
    }
    if (categoryRes.status === "fulfilled") {
      setCategories(Array.isArray(categoryRes.value.data) ? categoryRes.value.data : []);
    }
    if (productSummaryRes.status === "fulfilled") {
      setProductSummary(Array.isArray(productSummaryRes.value.data) ? productSummaryRes.value.data : []);
    }
    if (allocationSummaryRes.status === "fulfilled") {
      setAllocationSummary(Array.isArray(allocationSummaryRes.value.data) ? allocationSummaryRes.value.data : []);
    }

    try {
      const mfRes = await axios.get(BOARDROOM_MEETING_FILE_API);
      const mfData = mfRes.data?.data || mfRes.data || [];
      setMeetingFiles(Array.isArray(mfData) ? mfData : []);
    } catch(e) { setMeetingFiles([]); }

    const firstFailure = [taskRes, categoryRes, productSummaryRes, allocationSummaryRes].find((r) => r.status === "rejected");
    if (firstFailure) {
      alert(firstFailure.reason?.response?.data?.error || firstFailure.reason?.response?.data?.message || "Failed to load some dashboard data");
    }

    setLoading(false);
  };

  // Tied to each data refresh (not every render) so every "today"-based
  // calculation below stays stable while the user is interacting with
  // filters/tabs/search, instead of recomputing every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [tasks]);
  const yesterday = useMemo(() => { const d = new Date(now); d.setDate(d.getDate()-1); return d; }, [now]);
  const greeting = useMemo(() => { const h = now.getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; }, [now]);
  const todayLabel = useMemo(() => now.toLocaleDateString("en-IN", { weekday:"long", day:"2-digit", month:"long", year:"numeric" }), [now]);
  const technicianName = user?.name || (user?.email ? user.email.split("@")[0] : "Technician");

  const scrollToMyTasks = () => myTasksRef.current?.scrollIntoView({ behavior:"smooth", block:"start" });
  const scrollToSchedule = () => scheduleRef.current?.scrollIntoView({ behavior:"smooth", block:"start" });

  /* ── KPI numbers (always "today", unaffected by the global filter bar) ── */
  const assignedTodayCount = useMemo(()=>tasks.filter(t=>isSameDay(t.created_at, now)).length,[tasks,now]);
  const dueTodayCount = useMemo(()=>tasks.filter(t=>isSameDay(t.due_at, now) && !isDoneStatus(normalizeStatus(t.status))).length,[tasks,now]);
  const overdueCount = useMemo(()=>tasks.filter(t=>isOverdue(t, now)).length,[tasks,now]);
  const inProgressCount = useMemo(()=>tasks.filter(t=>normalizeStatus(t.status)==="in_progress").length,[tasks]);
  const pendingCount = useMemo(()=>tasks.filter(t=>normalizeStatus(t.status)==="pending").length,[tasks]);
  const completedTodayCount = useMemo(()=>tasks.filter(t=>{ const s=normalizeStatus(t.status); return isDoneStatus(s) && isSameDay(t.completed_at||t.updated_at, now); }).length,[tasks,now]);
  const completedYesterdayCount = useMemo(()=>tasks.filter(t=>{ const s=normalizeStatus(t.status); return isDoneStatus(s) && isSameDay(t.completed_at||t.updated_at, yesterday); }).length,[tasks,yesterday]);
  const completedDeltaLabel = useMemo(() => {
    if (completedYesterdayCount === 0) return completedTodayCount === 0 ? null : `+${completedTodayCount} vs yesterday`;
    const pct = Math.round(((completedTodayCount - completedYesterdayCount) / completedYesterdayCount) * 100);
    if (pct === 0) return "Same as yesterday";
    return `${pct > 0 ? "+" : ""}${pct}% vs yesterday`;
  }, [completedTodayCount, completedYesterdayCount]);
  const slaAtRiskCount = useMemo(()=>tasks.filter(t=>{ const s=normalizeStatus(t.status); if(isDoneStatus(s)) return false; return getTatInfo(t, now).tone === "amber"; }).length,[tasks,now]);

  const kpiCards = [
    { key:"assigned", label:"Assigned Today", value:assignedTodayCount, sub:"Tasks assigned today", tone:"info", Icon:FaCalendarDay, tab:"all" },
    { key:"due", label:"Due Today", value:dueTodayCount, sub:"Require completion today", tone:"warning", Icon:FaClipboardList, tab:"due_today" },
    { key:"overdue", label:"Overdue", value:overdueCount, sub:"Past their due time", tone: overdueCount>0?"danger":"neutral", Icon:FaExclamationTriangle, tab:"overdue" },
    { key:"progress", label:"In Progress", value:inProgressCount, sub:"Currently being worked on", tone:"info", Icon:FaSpinner, tab:"in_progress" },
    { key:"completed", label:"Completed Today", value:completedTodayCount, sub:completedDeltaLabel||"No tasks completed yet", tone:"success", Icon:FaCheckCircle, tab:"completed" },
    { key:"sla", label:"SLA At Risk", value:slaAtRiskCount, sub:"Due within next 2 hours", tone: slaAtRiskCount>0?"warning":"neutral", Icon:FaHourglassHalf, tab:"due_today" },
  ];

  /* ── Attention Required (section 7) -- derived from the same numbers, no separate fetch ── */
  const attentionItems = useMemo(() => {
    const items = [];
    if (overdueCount > 0) items.push({ key:"overdue", icon:"fa-triangle-exclamation", tone:"danger", text:`${overdueCount} task${overdueCount===1?" is":"s are"} overdue`, action:()=>{ setMyTab("overdue"); scrollToMyTasks(); } });
    if (slaAtRiskCount > 0) items.push({ key:"sla", icon:"fa-hourglass-half", tone:"warning", text:`${slaAtRiskCount} ticket${slaAtRiskCount===1?"":"s"} will breach SLA within 2 hours`, action:()=>{ setMyTab("due_today"); scrollToMyTasks(); } });
    if (pendingCount > 0) items.push({ key:"pending", icon:"fa-clock", tone:"info", text:`${pendingCount} task${pendingCount===1?" is":"s are"} pending and need${pendingCount===1?"s":""} action`, action:()=>{ setMyTab("all"); setFilterStatus("pending"); scrollToMyTasks(); } });
    if (dueTodayCount > 0) items.push({ key:"schedule", icon:"fa-calendar-day", tone:"info", text:`${dueTodayCount} task${dueTodayCount===1?"":"s"} scheduled for completion today`, action:scrollToSchedule });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueCount, slaAtRiskCount, pendingCount, dueTodayCount]);

  /* ── Global filter bar -- drives the browsing widgets below ── */
  const unitOptions = useMemo(()=>Array.from(new Set(tasks.map(t=>t.unit_name).filter(Boolean))).sort(),[tasks]);
  const statusOptionsPresent = useMemo(()=>STATUS_ORDER.filter(s=>tasks.some(t=>normalizeStatus(t.status)===s)),[tasks]);
  const hasActiveFilters = filterPeriod!=="all"||filterStatus!=="ALL"||filterPriority!=="ALL"||filterCategory!=="ALL"||filterUnit!=="ALL";
  const resetFilters = () => { setFilterPeriod("all"); setFilterCustomFrom(""); setFilterCustomTo(""); setFilterStatus("ALL"); setFilterPriority("ALL"); setFilterCategory("ALL"); setFilterUnit("ALL"); };

  const globalFilteredTasks = useMemo(() => {
    const [from, to] = periodRange(filterPeriod, filterCustomFrom, filterCustomTo, now);
    return tasks.filter((t) => {
      if (from || to) {
        const created = t.created_at ? new Date(t.created_at) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }
      if (filterStatus !== "ALL" && normalizeStatus(t.status) !== filterStatus) return false;
      if (filterPriority !== "ALL" && String(t.priority||"").toLowerCase() !== filterPriority) return false;
      if (filterCategory !== "ALL" && (t.category_name||t.category||"Uncategorized") !== filterCategory) return false;
      if (filterUnit !== "ALL" && (t.unit_name||"Unassigned") !== filterUnit) return false;
      return true;
    });
  }, [tasks, filterPeriod, filterCustomFrom, filterCustomTo, filterStatus, filterPriority, filterCategory, filterUnit, now]);

  /* ── My Tasks (section 8) ── */
  const myTabCounts = useMemo(() => ({
    all: globalFilteredTasks.length,
    due_today: globalFilteredTasks.filter(t=>isSameDay(t.due_at,now)&&!isDoneStatus(normalizeStatus(t.status))).length,
    in_progress: globalFilteredTasks.filter(t=>normalizeStatus(t.status)==="in_progress").length,
    overdue: globalFilteredTasks.filter(t=>isOverdue(t,now)).length,
    completed: globalFilteredTasks.filter(t=>isDoneStatus(normalizeStatus(t.status))).length,
  }), [globalFilteredTasks, now]);

  const myTasksTabFiltered = useMemo(() => globalFilteredTasks.filter((t) => {
    const s = normalizeStatus(t.status);
    if (myTab==="due_today") return isSameDay(t.due_at,now) && !isDoneStatus(s);
    if (myTab==="in_progress") return s==="in_progress";
    if (myTab==="overdue") return isOverdue(t,now);
    if (myTab==="completed") return isDoneStatus(s);
    return true;
  }), [globalFilteredTasks, myTab, now]);

  const myTasksSearched = useMemo(() => {
    const q = myTasksSearch.trim().toLowerCase();
    if (!q) return myTasksTabFiltered;
    return myTasksTabFiltered.filter(t => [t.title,t.description,t.unit_name,t.category_name,t.sub_category_name].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [myTasksTabFiltered, myTasksSearch]);

  const myTasksSorted = useMemo(() => {
    const rows = [...myTasksSearched];
    if (mySort === "priority") rows.sort((a,b)=>(PRIORITY_ORDER[String(a.priority||"").toLowerCase()]??9)-(PRIORITY_ORDER[String(b.priority||"").toLowerCase()]??9));
    else if (mySort === "due") rows.sort((a,b)=>{ const av=a.due_at?new Date(a.due_at).getTime():Infinity; const bv=b.due_at?new Date(b.due_at).getTime():Infinity; return av-bv; });
    else rows.sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
    return rows;
  }, [myTasksSearched, mySort]);

  const myTotalPages = Math.max(1, Math.ceil(myTasksSorted.length / MY_PAGE_SIZE));
  const myPageClamped = Math.min(myPage, myTotalPages);
  const myTasksPageRows = useMemo(()=>myTasksSorted.slice((myPageClamped-1)*MY_PAGE_SIZE, myPageClamped*MY_PAGE_SIZE), [myTasksSorted, myPageClamped]);

  /* ── Today's Schedule (section 9) ── */
  const scheduleRows = useMemo(() => tasks
    .filter(t => t.due_at && isSameDay(t.due_at, now) && !isDoneStatus(normalizeStatus(t.status)))
    .sort((a,b)=> new Date(a.due_at) - new Date(b.due_at)), [tasks, now]);

  /* ── Status Distribution donut (section 13) ── */
  const donutStatusRows = useMemo(() => {
    const map = new Map();
    globalFilteredTasks.forEach(t=>{ const s=normalizeStatus(t.status); map.set(s,(map.get(s)||0)+1); });
    return [...map.entries()].map(([s,c])=>({key:s,label:titleCase(s),count:c}))
      .sort((a,b)=>{ const ai=STATUS_ORDER.indexOf(a.key),bi=STATUS_ORDER.indexOf(b.key); if(ai===-1&&bi===-1) return a.label.localeCompare(b.label); if(ai===-1) return 1; if(bi===-1) return -1; return ai-bi; });
  }, [globalFilteredTasks]);
  const donutTotal = donutStatusRows.reduce((s,r)=>s+r.count,0);
  const donutChartData = { labels: donutStatusRows.map(r=>r.label), datasets:[{ data: donutStatusRows.map(r=>r.count), backgroundColor: donutStatusRows.map(r=>colorForStatusKey(r.key)), borderWidth:2, borderColor:"#fff" }] };
  const donutChartOpts = { responsive:true, maintainAspectRatio:false, cutout:"68%", plugins:{ legend:{display:false}, tooltip:{callbacks:{label:(ctx)=>` ${ctx.label}: ${ctx.raw} (${donutTotal?Math.round(ctx.raw/donutTotal*100):0}%)`}} } };

  /* ── Task Status horizontal bar (section 14) ── */
  const taskStatusBarRows = useMemo(() => ([
    { label:"Assigned", count: globalFilteredTasks.filter(t=>["assigned","open"].includes(normalizeStatus(t.status))).length, tone:"info" },
    { label:"Pending", count: globalFilteredTasks.filter(t=>normalizeStatus(t.status)==="pending").length, tone:"warning" },
    { label:"In Progress", count: globalFilteredTasks.filter(t=>normalizeStatus(t.status)==="in_progress").length, tone:"info" },
    { label:"Completed", count: globalFilteredTasks.filter(t=>isDoneStatus(normalizeStatus(t.status))).length, tone:"success" },
    { label:"Overdue", count: globalFilteredTasks.filter(t=>isOverdue(t,now)).length, tone:"danger" },
  ]), [globalFilteredTasks, now]);
  const taskStatusBarData = { labels: taskStatusBarRows.map(r=>r.label), datasets:[{ data: taskStatusBarRows.map(r=>r.count), backgroundColor: taskStatusBarRows.map(r=>TONE_HEX[r.tone]), borderRadius:6, borderSkipped:false, barThickness:22 }] };
  const taskStatusBarOpts = { indexAxis:"y", responsive:true, maintainAspectRatio:false, plugins:{ legend:{display:false}, tooltip:{callbacks:{label:(ctx)=>`${ctx.raw} task${ctx.raw===1?"":"s"}`}} }, scales:{ x:{ beginAtZero:true, ticks:{ precision:0, font:{size:10} } }, y:{ ticks:{ font:{size:11,weight:"700"} } } } };

  /* ── Tasks by Category (section 15) ── */
  const categoryRows = useMemo(()=>categories.map((item,i)=>({
    key:`${item.category||item.name||i}-${i}`,
    name: item.category||item.category_name||item.name||item.sub_category_name||`Category ${i+1}`,
    count: safeNumber(item.count??item.total??item.task_count),
  })),[categories]);
  const categoryTotal = categoryRows.reduce((s,c)=>s+c.count,0) || 1;

  /* ── Upcoming Meetings (section 16) -- ordered (and filtered as
     upcoming/"Next") by actual start date+time, not by date alone, so two
     meetings on the same day never get sorted out of time order and a
     past meeting (e.g. last month's) never gets tagged "Next". ── */
  const sortedMeetingFiles = useMemo(()=>{
    const nowMs = now.getTime();
    return meetingFiles
      .map(f=>({ f, ts: meetingStartTimestamp(f) }))
      .filter(({ts})=>ts != null && ts >= nowMs)
      .sort((a,b)=>a.ts-b.ts)
      .map(({f})=>f);
  },[meetingFiles, now]);
  const upcomingMeetings = meetingsExpanded ? sortedMeetingFiles : sortedMeetingFiles.slice(0,5);

  /* ── Product / Allocation overview (section 17) ── */
  const inventoryStats = useMemo(()=>({
    inventoryValue: productSummary.reduce((s,i)=>s+safeNumber(i.current_value),0),
    totalProducts: productSummary.reduce((s,i)=>s+safeNumber(i.total_products),0),
    totalStock: productSummary.reduce((s,i)=>s+safeNumber(i.stock_qty),0),
    currentlyOut: allocationSummary.reduce((s,i)=>s+safeNumber(i.currently_out),0),
    totalReturned: allocationSummary.reduce((s,i)=>s+safeNumber(i.returned_qty??i.total_returned),0),
  }),[productSummary, allocationSummary]);
  const topProductUnits = useMemo(()=>[...productSummary].sort((a,b)=>safeNumber(b.total_products)-safeNumber(a.total_products)).slice(0,5),[productSummary]);
  const topAllocationUnits = useMemo(()=>[...allocationSummary].sort((a,b)=>safeNumber(b.currently_out)-safeNumber(a.currently_out)).slice(0,5),[allocationSummary]);

  /* ── Range Tasks bar chart -- same status-tone bucketing/geometry as the
     Task Status horizontal bar (section 14), scoped to the selected day
     range via this card's own dropdown. ── */
  const [range, setRange] = useState(30);
  const rangeTasks = useMemo(() => {
    const from = new Date(now);
    from.setDate(from.getDate() - range);
    from.setHours(0, 0, 0, 0);
    return tasks.filter((t) => {
      const c = t.created_at ? new Date(t.created_at) : null;
      return c && !Number.isNaN(c.getTime()) && c >= from && c <= now;
    });
  }, [tasks, range, now]);
  const rangeStatusRows = useMemo(() => ([
    { label:"Assigned", count: rangeTasks.filter(t=>["assigned","open"].includes(normalizeStatus(t.status))).length, tone:"info" },
    { label:"Pending", count: rangeTasks.filter(t=>normalizeStatus(t.status)==="pending").length, tone:"warning" },
    { label:"In Progress", count: rangeTasks.filter(t=>normalizeStatus(t.status)==="in_progress").length, tone:"info" },
    { label:"Completed", count: rangeTasks.filter(t=>isDoneStatus(normalizeStatus(t.status))).length, tone:"success" },
    { label:"Overdue", count: rangeTasks.filter(t=>isOverdue(t,now)).length, tone:"danger" },
  ]), [rangeTasks, now]);
  const barData = { labels: rangeStatusRows.map(r=>r.label), datasets:[{ data: rangeStatusRows.map(r=>r.count), backgroundColor: rangeStatusRows.map(r=>TONE_HEX[r.tone]), borderRadius:6, borderSkipped:false, barThickness:22 }] };
  const barOpts = { indexAxis:"y", responsive:true, maintainAspectRatio:false, plugins:{ legend:{display:false}, tooltip:{callbacks:{label:(ctx)=>`${ctx.raw} task${ctx.raw===1?"":"s"}`}} }, scales:{ x:{ beginAtZero:true, ticks:{ precision:0, font:{size:10} } }, y:{ ticks:{ font:{size:11,weight:"700"} } } } };

  /* ── Recent Tasks table -- its own search/status filter, separate from
     the global filter bar (same pattern as My Tasks' own search/sort). ── */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const statusRows = useMemo(
    () => STATUS_ORDER.filter((s) => globalFilteredTasks.some((t) => normalizeStatus(t.status) === s)).map((s) => ({ key: s, label: titleCase(s) })),
    [globalFilteredTasks]
  );
  const recentTasks = useMemo(
    () => [...globalFilteredTasks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [globalFilteredTasks]
  );
  const filteredTasks = useMemo(() => {
    let rows = recentTasks;
    if (statusFilter !== "ALL") rows = rows.filter((t) => normalizeStatus(t.status) === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((t) => [taskDisplayTitle(t), t.description].filter(Boolean).join(" ").toLowerCase().includes(q));
    return rows;
  }, [recentTasks, search, statusFilter]);
  const completionRate = useMemo(() => {
    if (!globalFilteredTasks.length) return 0;
    const done = globalFilteredTasks.filter((t) => isDoneStatus(normalizeStatus(t.status))).length;
    return Math.round((done / globalFilteredTasks.length) * 100);
  }, [globalFilteredTasks]);

  return (
    <div className="td-page">
      {/* Compact premium welcome header */}
      <section className="td-hero">
        <div className="td-hero-copy">
          <div className="td-eyebrow">Technician Panel</div>
          <h1 className="td-title">
            {greeting}, <span>{technicianName}</span>
          </h1>
          <div className="td-hero-meta">
            <span className="td-welcome-date">
              <i className="fa-regular fa-calendar" aria-hidden="true"></i>
              {todayLabel}
            </span>
            <span className="td-hero-separator" aria-hidden="true">•</span>
            <span className="td-hero-status">
              <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
              Dashboard synced with your assigned work
            </span>
          </div>
        </div>

        <div className="td-hactions">
          <Button
            variant={null}
            className="td-btn td-btn-ghost"
            onClick={fetchDashboardData}
            disabled={loading}
            aria-label="Refresh dashboard"
          >
            <i className={`fa-solid fa-rotate${loading ? " fa-spin" : ""}`} aria-hidden="true"></i>
            Refresh
          </Button>
          <Button variant={null} className="td-btn td-btn-soft" onClick={scrollToSchedule}>
            <i className="fa-regular fa-calendar-check" aria-hidden="true"></i>
            Today's Schedule
          </Button>
          <Button variant={null} className="td-btn td-btn-primary" onClick={()=>navigate("/technician/tasks")}>
            View My Tasks <FaArrowRight />
          </Button>
        </div>
      </section>

      {/* Six equal KPI cards */}
      <section className="td-kpi-strip" aria-label="Today's task summary">
        {loading && !tasks.length ? (
          Array.from({length:6}).map((_,i)=><div key={i} className="td-skeleton-kpi" />)
        ) : kpiCards.map((card)=>{
          const Icon=card.Icon;
          return (
            <button
              type="button"
              className={`td-kpi td-kpi-tone-${card.tone} td-kpi--clickable`}
              key={card.key}
              onClick={()=>{ setMyTab(card.tab); scrollToMyTasks(); }}
            >
              <span className="td-kpi-icon"><Icon/></span>
              <span className="td-kpi-content">
                <span className="td-kpi-label">{card.label}</span>
                <strong className="td-kpi-val">{card.value}</strong>
                <span className="td-kpi-sub">{card.sub}</span>
              </span>
              <span className="td-kpi-arrow"><i className="fa-solid fa-arrow-right" aria-hidden="true"></i></span>
            </button>
          );
        })}
      </section>

      {/* Inventory Summary + Today's Schedule + Upcoming Meetings -- one
          compact operations row (desktop ratio 2fr/1fr/1fr) right after the
          KPI strip, replacing the old layout where Inventory Summary was a
          full-width row on its own and Schedule/Meetings sat in a separate
          row further down the page. Needs Your Attention (previously
          paired with the Inventory card) stays below with the rest of the
          page. Inventory Summary is hidden entirely (not just zeroed out)
          unless this caller has legitimate Product or Allocation
          visibility -- see moduleVisibility above; showing an empty/zero
          inventory summary to a Technician with no assignment would still
          be exposing org-wide-shaped inventory framing they have no
          legitimate access to. When it's hidden, .td-operations-row--no-
          inventory switches the row to an even 2-column split instead of
          leaving the 2fr column's space empty. */}
      <section className={`td-operations-row${(moduleVisibility.products || moduleVisibility.allocations) ? "" : " td-operations-row--no-inventory"}`}>
        {(moduleVisibility.products || moduleVisibility.allocations) && (
          <section className="td-card td-inventory-card">
            <div className="td-ch">
              <div className="td-section-heading">
                <span className="td-section-icon td-section-icon--money"><FaRupeeSign/></span>
                <div>
                  <strong>Inventory Summary</strong>
                  <small>Products allocated across your units</small>
                </div>
              </div>
              <Button variant={null} className="td-link-btn" onClick={()=>navigate("/technician/products")}>
                View Products <FaArrowRight/>
              </Button>
            </div>

            <div className="td-inventory-value">
              <span>Total Inventory Value</span>
              <strong title={`₹${formatMoney(inventoryStats.inventoryValue)}`}>&#8377;{formatMoney(inventoryStats.inventoryValue)}</strong>
            </div>

            <div className="td-mini-stats">
              <div>
                <span>Products</span>
                <strong>{inventoryStats.totalProducts}</strong>
              </div>
              <div>
                <span>Stock Qty</span>
                <strong>{inventoryStats.totalStock}</strong>
              </div>
              <div>
                <span>Currently Out</span>
                <strong>{inventoryStats.currentlyOut}</strong>
              </div>
              <div>
                <span>Returned</span>
                <strong>{inventoryStats.totalReturned}</strong>
              </div>
            </div>
          </section>
        )}

        <section className="td-card" ref={scheduleRef}>
          <div className="td-ch">
            <div className="td-section-heading">
              <span className="td-section-icon td-section-icon--calendar">
                <i className="fa-regular fa-calendar-check" aria-hidden="true"></i>
              </span>
              <div>
                <strong>Today's Schedule</strong>
                <small>{todayLabel}</small>
              </div>
            </div>
            <span className="td-count-badge td-count-badge--neutral">{scheduleRows.length}</span>
          </div>

          <div className="td-schedule-list">
            {loading && !tasks.length ? (
              <div className="td-loader"><Spinner animation="border" size="sm"/></div>
            ) : scheduleRows.length ? scheduleRows.map(task=>(
              <div className="td-schedule-row" key={task.id}>
                <div className="td-schedule-time">
                  {new Date(task.due_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}
                </div>
                <div className="td-schedule-line">
                  <span className="td-schedule-dot" aria-hidden="true" />
                </div>
                <div className="td-schedule-body">
                  <span className="td-cm" title={taskDisplayTitle(task)}>{taskDisplayTitle(task)}</span>
                  <small>{task.unit_name || "-"} · {task.category_name || task.category || "Uncategorized"}</small>
                </div>
                <StatusPill status={task.status} />
                <Button variant={null} size="sm" className="td-btn td-btn-table" onClick={()=>navigate("/technician/tasks")}>View</Button>
              </div>
            )) : (
              <div className="td-empty td-empty--compact">
                <div className="td-ei"><i className="fa-solid fa-calendar-check" aria-hidden="true"></i></div>
                <p>Nothing scheduled for today.</p>
              </div>
            )}
          </div>
        </section>

        <section className="td-card">
          <div className="td-ch">
            <div className="td-section-heading">
              <span className="td-section-icon td-section-icon--calendar">
                <i className="fa-solid fa-users" aria-hidden="true"></i>
              </span>
              <div><strong>Upcoming Meetings</strong><small>Latest meeting-file schedule</small></div>
            </div>
            <Button variant={null} className="td-icon-btn" onClick={fetchDashboardData} disabled={loading} aria-label="Refresh meetings">
              <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            </Button>
          </div>

          <div className="td-meeting-list">
            {loading && !meetingFiles.length ? (
              <div className="td-loader"><Spinner animation="border" size="sm"/></div>
            ) : upcomingMeetings.length ? upcomingMeetings.map((f,i)=>(
              <div className={`td-meeting-row${i===0?" td-meeting-row--next":""}`} key={f.file_id||`${f.booking_id}-${f.meeting_file_code}`}>
                <div className="td-meeting-datebox">
                  <strong>{f.meeting_date ? new Date(f.meeting_date).toLocaleDateString("en-IN",{day:"2-digit"}) : "--"}</strong>
                  <span>{f.meeting_date ? new Date(f.meeting_date).toLocaleDateString("en-IN",{month:"short"}) : "---"}</span>
                </div>
                <div className="td-meeting-main">
                  <div>
                    {i===0 && <span className="td-meeting-next-tag">Next</span>}
                    <span className="td-cm" title={f.meeting_name||"-"}>{f.meeting_name||"-"}</span>
                  </div>
                  <small>{f.meeting_time||"-"} · {formatDate(f.meeting_date)}</small>
                </div>
                <Badge bg={null} className="td-badge td-badge-green">{f.meeting_file_code||"-"}</Badge>
              </div>
            )) : (
              <div className="td-empty td-empty--compact"><div className="td-ei"><i className="fa-solid fa-file-lines" aria-hidden="true"></i></div><p>No upcoming meetings</p></div>
            )}
          </div>

          {sortedMeetingFiles.length > 5 && (
            <div className="td-foot">
              <span>Showing {upcomingMeetings.length} of {sortedMeetingFiles.length}</span>
              <Button variant={null} className="td-link-btn" onClick={()=>setMeetingsExpanded(v=>!v)}>
                {meetingsExpanded?"Show Less":"View All"}
              </Button>
            </div>
          )}
        </section>
      </section>

      {/* Analytics -- promoted to the top so the at-a-glance charts and
          upcoming meetings are visible before scrolling past the filters/
          task list. Markup unchanged from its previous position further
          down the page; only its position moved. */}
      <section className="td-section-block">
        <div className="td-section-bar">
          <div>
            <span className="td-eyebrow">Analytics</span>
            <h3>Workload & Task Health</h3>
          </div>
          <span>{globalFilteredTasks.length} filtered tasks</span>
        </div>

        <div className="td-analytics-grid">
          <div className="td-card">
            <div className="td-ch">
              <div className="td-section-heading">
                <span className="td-section-icon"><FaChartPie/></span>
                <div><strong>Status Distribution</strong><small>Current filtered workload</small></div>
              </div>
            </div>

            {loading && !tasks.length ? (
              <div className="td-loader"><Spinner animation="border" size="sm"/></div>
            ) : donutStatusRows.length > 1 ? (
              <>
                <div className="td-chartbox td-chartbox--donut">
                  <div className="td-donut-center"><span>Total Tasks</span><strong>{donutTotal}</strong></div>
                  <Pie data={donutChartData} options={donutChartOpts} />
                </div>
                <div className="td-donut-legend">
                  {donutStatusRows.map(r=>(
                    <div className="td-donut-legend-row" key={r.key}>
                      <span className="td-donut-dot" style={{background:colorForStatusKey(r.key)}} />
                      <span className="td-cm">{r.label}</span>
                      <strong>{r.count}</strong>
                      <span>{donutTotal?Math.round(r.count/donutTotal*100):0}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : donutStatusRows.length === 1 ? (
              <div className="td-empty td-empty--chart">
                <div className="td-ei"><i className="fa-solid fa-circle-check" aria-hidden="true"></i></div>
                <p>All {donutTotal} task{donutTotal===1?"":"s"} are currently <strong>{donutStatusRows[0].label}</strong>.</p>
              </div>
            ) : (
              <div className="td-empty td-empty--chart"><div className="td-ei"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div><p>No tasks match the current filters.</p></div>
            )}
          </div>

          <div className="td-card">
            <div className="td-ch">
              <div className="td-section-heading">
                <span className="td-section-icon td-section-icon--blue">
                  <i className="fa-solid fa-chart-bar" aria-hidden="true"></i>
                </span>
                <div><strong>Task Status</strong><small>Filtered workload by state</small></div>
              </div>
            </div>
            <div className="td-chartbox">
              {loading ? <div className="td-loader"><Spinner animation="border" size="sm"/></div> : (
                <Bar data={taskStatusBarData} options={taskStatusBarOpts} plugins={[barValuePlugin]}/>
              )}
            </div>
          </div>

          <div className="td-card">
            <div className="td-ch">
              <div className="td-section-heading">
                <span className="td-section-icon td-section-icon--purple">
                  <i className="fa-solid fa-chart-line" aria-hidden="true"></i>
                </span>
                <div><strong>Range Tasks</strong><small>Status summary for selected period</small></div>
              </div>
              <Form.Select className="td-sel td-range-select" value={range} onChange={(e)=>setRange(Number(e.target.value))}>
                <option value={7}>7 Days</option>
                <option value={15}>15 Days</option>
                <option value={30}>30 Days</option>
                <option value={90}>90 Days</option>
              </Form.Select>
            </div>
            <div className="td-chartbox">
              {loading ? <div className="td-loader"><Spinner animation="border" size="sm"/></div> : (
                <Bar data={barData} options={barOpts} plugins={[barValuePlugin]}/>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Performance / User Satisfaction -- 2-column row. Reuses
          .td-analytics-grid for its responsive column layout (Performance
          and Satisfaction panels render their own top-level .td-card, so no
          extra wrapper is needed as a grid item). */}
      <section className="td-analytics-grid td-analytics-grid--2col">
        <TechnicianPerformancePanel />

        {/* Phase 26: technician task-completion -> User Satisfaction feedback.
            Own-technician data only (self-scoped server-side); reuses the
            same KPI8 evidence resolver the Developer Home dashboard uses --
            never a second scoring implementation. */}
        <TechnicianSatisfactionPanel />
      </section>

      {/* Phase 52: New Joiner Asset Request -- own self-contained section,
          own data fetch, canonical components/ui primitives (StatCard/
          DataTable/Button) rather than this page's bespoke td-* markup. */}
      <NewJoinerDeploymentSection />

      {/* Compact filters - one toolbar on desktop */}
      <section className="td-card td-filterbar">
        <div className="td-filterbar-main">
          <div className="td-filter-title">
            <span className="td-filter-icon"><i className="fa-solid fa-sliders" aria-hidden="true"></i></span>
            <div>
              <strong>Dashboard Filters</strong>
              <small>{globalFilteredTasks.length} task{globalFilteredTasks.length===1?"":"s"} in current view</small>
            </div>
          </div>

          <div className="td-pill-group" role="group" aria-label="Date range">
            {PERIOD_OPTIONS.map(([key,label])=>(
              <button
                key={key}
                type="button"
                aria-pressed={filterPeriod===key}
                className={`td-pill-btn${filterPeriod===key?" td-pill-btn--active":""}`}
                onClick={()=>setFilterPeriod(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="td-filter-controls">
            {filterPeriod === "custom" && (
              <div className="td-custom-range">
                <Form.Control
                  type="date"
                  className="td-sel td-date"
                  value={filterCustomFrom}
                  onChange={e=>setFilterCustomFrom(e.target.value)}
                  aria-label="Custom range from"
                />
                <span>to</span>
                <Form.Control
                  type="date"
                  className="td-sel td-date"
                  value={filterCustomTo}
                  onChange={e=>setFilterCustomTo(e.target.value)}
                  aria-label="Custom range to"
                />
              </div>
            )}

            <Form.Select className="td-sel td-filter-select" aria-label="Filter by status" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="ALL">All Status</option>
              {statusOptionsPresent.map(s=><option key={s} value={s}>{titleCase(s)}</option>)}
            </Form.Select>

            <Form.Select className="td-sel td-filter-select" aria-label="Filter by priority" value={filterPriority} onChange={e=>setFilterPriority(e.target.value)}>
              <option value="ALL">All Priority</option>
              {["critical","high","medium","low"].map(p=><option key={p} value={p}>{titleCase(p)}</option>)}
            </Form.Select>

            <Form.Select className="td-sel td-filter-select td-filter-select--wide" aria-label="Filter by category" value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}>
              <option value="ALL">All Categories</option>
              {categoryRows.map(c=><option key={c.key} value={c.name}>{c.name}</option>)}
            </Form.Select>

            <Form.Select className="td-sel td-filter-select" aria-label="Filter by unit" value={filterUnit} onChange={e=>setFilterUnit(e.target.value)}>
              <option value="ALL">All Units</option>
              {unitOptions.map(u=><option key={u} value={u}>{u}</option>)}
            </Form.Select>

            {hasActiveFilters && (
              <Button variant={null} className="td-btn td-btn-reset" onClick={resetFilters}>
                <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
                Reset
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Needs Your Attention -- previously paired with Inventory Summary in
          a two-column grid; Inventory Summary moved up near the KPI strip,
          so this card now stands alone at full width. */}
      <section className="td-card td-attention">
        <div className="td-ch">
          <div className="td-section-heading">
            <span className="td-section-icon td-section-icon--alert">
              <i className="fa-solid fa-bell" aria-hidden="true"></i>
            </span>
            <div>
              <strong>Needs Your Attention</strong>
              <small>Items that may require immediate action</small>
            </div>
          </div>
          {attentionItems.length > 0 && <span className="td-count-badge">{attentionItems.length}</span>}
        </div>

        <div className="td-attention-list">
          {attentionItems.length ? attentionItems.map(item=>(
            <button key={item.key} type="button" className={`td-attention-row td-attention-row--${item.tone}`} onClick={item.action}>
              <span className="td-attention-icon"><i className={`fa-solid ${item.icon}`} aria-hidden="true"></i></span>
              <span className="td-attention-text">{item.text}</span>
              <i className="fa-solid fa-chevron-right td-attention-arrow" aria-hidden="true"></i>
            </button>
          )) : (
            <div className="td-attention-row td-attention-row--success td-attention-row--static">
              <span className="td-attention-icon"><i className="fa-solid fa-circle-check" aria-hidden="true"></i></span>
              <span className="td-attention-text">
                <strong>You're all caught up.</strong>
                <small>No overdue, SLA-risk, pending, or due-today task needs attention right now.</small>
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Primary work table */}
      <section className="td-card td-mytasks" ref={myTasksRef}>
        <div className="td-ch td-ch--wrap td-mytasks-head">
          <div className="td-section-heading">
            <span className="td-section-icon">
              <i className="fa-solid fa-list-check" aria-hidden="true"></i>
            </span>
            <div>
              <strong>My Tasks</strong>
              <small>Review, prioritize and work on assigned tickets</small>
            </div>
          </div>

          <div className="td-srow">
            <InputGroup className="td-search">
              <InputGroup.Text>
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Search tasks…"
                aria-label="Search my tasks"
                value={myTasksSearch}
                onChange={(e)=>setMyTasksSearch(e.target.value)}
              />
              {myTasksSearch&&(
                <Button variant={null} className="td-search-clear" onClick={()=>setMyTasksSearch("")} aria-label="Clear search">
                  <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                </Button>
              )}
            </InputGroup>

            <Form.Select className="td-sel td-sort" aria-label="Sort my tasks" value={mySort} onChange={(e)=>setMySort(e.target.value)}>
              <option value="latest">Latest Assigned</option>
              <option value="priority">Priority</option>
              <option value="due">Due Date</option>
            </Form.Select>

            <Button variant={null} className="td-btn td-btn-soft" onClick={()=>navigate("/technician/tasks")}>
              View All <FaArrowRight/>
            </Button>
          </div>
        </div>

        <div className="td-tabs" role="group" aria-label="Filter my tasks">
          {MY_TASK_TABS.map(([key,label])=>(
            <button
              key={key}
              type="button"
              aria-pressed={myTab===key}
              className={`td-tab${myTab===key?" td-tab--active":""}`}
              onClick={()=>setMyTab(key)}
            >
              {label} <span className="td-tab-count">{myTabCounts[key]}</span>
            </button>
          ))}
        </div>

        <div className="td-twrap">
          {loading && !tasks.length ? (
            <div className="td-skeleton-table">{Array.from({length:5}).map((_,i)=><div key={i} className="td-skeleton-row" />)}</div>
          ) : myTasksPageRows.length ? (
            <table className="td-table td-table--mytasks">
              <thead>
                <tr>
                  <th>Task ID</th>
                  <th>Task</th>
                  <th>Unit</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>SLA / Due</th>
                  <th>Assigned</th>
                  <th className="td-table-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {myTasksPageRows.map((task) => {
                  const tat = getTatInfo(task, now);
                  const rowClass = tat.tone === "red" ? "td-row-danger" : tat.tone === "amber" ? "td-row-warning" : "";
                  return (
                    <tr key={task.id} className={rowClass}>
                      <td><span className="td-rn">#{task.id}</span></td>
                      <td>
                        <div className="td-nb">
                          <div className="td-av">{taskDisplayTitle(task).charAt(0).toUpperCase()||"T"}</div>
                          <div className="td-task-copy">
                            <span className="td-cm" title={taskDisplayTitle(task)||"-"}>{taskDisplayTitle(task)||"-"}</span>
                            <small title={taskDescriptionText(task)||"—"}>{truncate(taskDescriptionText(task)||"—",48)}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="td-cm" title={task.unit_name||"-"}>{task.unit_name||"-"}</span></td>
                      <td>
                        <span className="td-cm" title={task.category_name||task.category||"-"}>{task.category_name||task.category||"-"}</span>
                        <small>{task.sub_category_name||task.sub_category||""}</small>
                      </td>
                      <td><PriorityPill priority={task.priority} /></td>
                      <td><StatusPill status={task.status} /></td>
                      <td><span className={`tlp-tat tlp-tat-${tat.tone}`}>{tat.label}</span></td>
                      <td className="td-nowrap">{formatDate(task.created_at)}</td>
                      <td className="td-table-action">
                        <Button variant={null} size="sm" className="td-btn td-btn-table" onClick={()=>navigate("/technician/tasks")}>
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="td-empty">
              <div className="td-ei"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div>
              <p>{myTasksSearch ? "No tasks match your search." : tasks.length ? "No tasks in this view." : "No tasks assigned to you yet."}</p>
            </div>
          )}
        </div>

        {!loading && myTasksSorted.length > 0 && (
          <div className="td-foot">
            <span>
              Showing <b>{(myPageClamped-1)*MY_PAGE_SIZE+1}</b>–<b>{Math.min(myPageClamped*MY_PAGE_SIZE,myTasksSorted.length)}</b> of <b>{myTasksSorted.length}</b>
            </span>
            <div className="td-page-btns">
              <Button variant={null} className="td-page-btn" disabled={myPageClamped<=1} onClick={()=>setMyPage(myPageClamped-1)} aria-label="Previous page">‹</Button>
              <span>{myPageClamped} / {myTotalPages}</span>
              <Button variant={null} className="td-page-btn" disabled={myPageClamped>=myTotalPages} onClick={()=>setMyPage(myPageClamped+1)} aria-label="Next page">›</Button>
            </div>
          </div>
        )}
      </section>

      {/* Tasks by Category -- previously paired with Upcoming Meetings in a
          two-column grid; Meetings moved up alongside Analytics, so this
          card now stands alone at full width. */}
      <section className="td-card">
        <div className="td-ch">
          <div className="td-section-heading">
            <span className="td-section-icon"><FaListUl/></span>
            <div><strong>Tasks by Category</strong><small>Click a category to filter the dashboard</small></div>
          </div>
        </div>

        <div className="td-category-list">
          {categoryRows.length ? categoryRows.map(cat=>{
            const pct = Math.round(cat.count/categoryTotal*100);
            const active = filterCategory === cat.name;
            return (
              <button
                type="button"
                key={cat.key}
                className={`td-category-row${active?" td-category-row--active":""}`}
                onClick={()=>setFilterCategory(active?"ALL":cat.name)}
                aria-pressed={active}
              >
                <div className="td-category-row-top">
                  <span className="td-cm">{cat.name}</span>
                  <span className="td-category-count">{cat.count} <small>{pct}%</small></span>
                </div>
                <div className="td-category-track"><div className="td-category-fill" style={{width:`${pct}%`}} /></div>
              </button>
            );
          }) : (
            <div className="td-empty td-empty--compact"><div className="td-ei"><i className="fa-solid fa-tags" aria-hidden="true"></i></div><p>No category data</p></div>
          )}
        </div>
      </section>

      {/* Product / allocation -- each card is only rendered when this
          caller has legitimate visibility for that module (moduleVisibility,
          from GET /technician-dashboard/module-visibility -- see fetch
          above). A Technician with no allocation of their own gets neither
          card, not an empty/zero one: there is a real difference between
          "I have access but zero records right now" and "nothing here was
          ever assigned to me at all" -- see td-grid2--single below for how
          the layout reflows instead of leaving a blank column. */}
      {(moduleVisibility.products || moduleVisibility.allocations) && (
        <section className={`td-grid2${(moduleVisibility.products && moduleVisibility.allocations) ? "" : " td-grid2--single"}`}>
          {moduleVisibility.products && (
            <div className="td-card">
              <div className="td-ch">
                <div className="td-section-heading">
                  <span className="td-section-icon td-section-icon--green">
                    <i className="fa-solid fa-boxes-stacked" aria-hidden="true"></i>
                  </span>
                  <div><strong>Product Overview</strong><small>Highest-volume units</small></div>
                </div>
                <Button variant={null} className="td-link-btn" onClick={()=>navigate("/technician/products")}>View All <FaArrowRight/></Button>
              </div>

              <div className="td-clist">
                {topProductUnits.length?topProductUnits.map((u)=>(
                  <div className="td-crow" key={u.unit_id}>
                    <span className="td-row-icon"><i className="fa-solid fa-warehouse" aria-hidden="true"></i></span>
                    <div className="td-crow-main">
                      <span className="td-cm" title={u.unit_name||"-"}>{u.unit_name||"-"}</span>
                      <small>{safeNumber(u.stock_qty)} in stock</small>
                    </div>
                    <Badge bg={null} className="td-badge td-badge-green">{safeNumber(u.total_products)} pcs</Badge>
                  </div>
                )):<div className="td-empty td-empty--compact"><div className="td-ei"><i className="fa-solid fa-box-open" aria-hidden="true"></i></div><p>No product data</p></div>}
              </div>
            </div>
          )}

          {moduleVisibility.allocations && (
            <div className="td-card">
              <div className="td-ch">
                <div className="td-section-heading">
                  <span className="td-section-icon td-section-icon--blue">
                    <i className="fa-solid fa-arrow-right-arrow-left" aria-hidden="true"></i>
                  </span>
                  <div><strong>Allocation Overview</strong><small>Items currently allocated by unit</small></div>
                </div>
                <Button variant={null} className="td-link-btn" onClick={()=>navigate("/technician/allocations/unit-wise")}>View All <FaArrowRight/></Button>
              </div>

              <div className="td-clist">
                {topAllocationUnits.length?topAllocationUnits.map((u)=>(
                  <div className="td-crow" key={u.unit_id}>
                    <span className="td-row-icon td-row-icon--blue"><i className="fa-solid fa-box" aria-hidden="true"></i></span>
                    <div className="td-crow-main">
                      <span className="td-cm" title={u.unit_name||"-"}>{u.unit_name||"-"}</span>
                      <small>Returned: {safeNumber(u.returned_qty||u.total_returned)}</small>
                    </div>
                    <Badge bg={null} className="td-badge td-badge-blue">Out: {safeNumber(u.currently_out)}</Badge>
                  </div>
                )):<div className="td-empty td-empty--compact"><div className="td-ei"><i className="fa-solid fa-clipboard-list" aria-hidden="true"></i></div><p>No allocation data</p></div>}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Meeting files + recent activity */}
      <section className="td-grid2 td-grid2--wide-left">
        <div className="td-card">
          <div className="td-ch td-ch--wrap">
            <div className="td-section-heading">
              <span className="td-section-icon td-section-icon--purple">
                <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
              </span>
              <div><strong>Recent Tasks</strong><small>Latest tickets in the current dashboard view</small></div>
            </div>

            <div className="td-srow">
              <InputGroup className="td-search td-search--small">
                <InputGroup.Text><i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i></InputGroup.Text>
                <Form.Control placeholder="Search…" aria-label="Search recent tasks" value={search} onChange={(e)=>setSearch(e.target.value)}/>
                {search&&(
                  <Button variant={null} className="td-search-clear" onClick={()=>setSearch("")} aria-label="Clear search" title="Clear search">
                    <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                  </Button>
                )}
              </InputGroup>

              <Form.Select className="td-sel td-filter-select" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
                <option value="ALL">All Status</option>
                {statusRows.map((r)=><option key={r.key} value={r.key}>{r.label}</option>)}
              </Form.Select>
            </div>
          </div>

          <div className="td-twrap">
            {loading ? (
              <div className="td-loader"><Spinner animation="border" size="sm"/><p>Loading…</p></div>
            ) : (
              <table className="td-table td-table--recent">
                <thead><tr><th>#</th><th>Task</th><th>Staff</th><th>Category</th><th>Status</th><th>Created</th></tr></thead>
                <tbody>
                  {filteredTasks.length ? filteredTasks.slice(0,5).map((task,i)=>(
                    <tr key={task.id||i}>
                      <td><span className="td-rn">#{task.id || i+1}</span></td>
                      <td>
                        <div className="td-nb">
                          <div className="td-av">{taskDisplayTitle(task).charAt(0).toUpperCase()||"T"}</div>
                          <div className="td-task-copy">
                            <span className="td-cm" title={taskDisplayTitle(task)||"-"}>{taskDisplayTitle(task)||"-"}</span>
                            <small title={taskDescriptionText(task)||"—"}>{truncate(taskDescriptionText(task)||"—",42)}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="td-cm" title={task.requested_for_name||"-"}>{task.requested_for_name||"-"}</span></td>
                      <td>
                        <span className="td-cm" title={task.category_name||task.category||"-"}>{task.category_name||task.category||"-"}</span>
                        <small>{task.sub_category_name||task.sub_category||"-"}</small>
                      </td>
                      <td><Badge bg={null} className={getBadgeClass(task.status)}>{titleCase(task.status)}</Badge></td>
                      <td className="td-nowrap">{task.created_at?new Date(task.created_at).toLocaleString():"-"}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6">
                      <div className="td-empty td-empty--compact">
                        <div className="td-ei"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div>
                        <p>No tasks found</p>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {!loading&&filteredTasks.length>5&&(
            <div className="td-foot">
              <span>Showing <b>5</b> of <b>{filteredTasks.length}</b></span>
              <Button variant={null} className="td-link-btn" onClick={()=>navigate("/technician/tasks")}>Full List <FaArrowRight/></Button>
            </div>
          )}
        </div>

        <div className="td-card">
          <div className="td-ch">
            <div className="td-section-heading">
              <span className="td-section-icon td-section-icon--green">
                <i className="fa-solid fa-file-lines" aria-hidden="true"></i>
              </span>
              <div><strong>Meeting Files</strong><small>Most recent boardroom files</small></div>
            </div>
            <Button variant={null} className="td-icon-btn" onClick={fetchDashboardData} disabled={loading}>
              <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            </Button>
          </div>

          <div className="td-meeting-body">
            {loading ? (
              <div className="td-loader"><Spinner animation="border" size="sm"/></div>
            ) : meetingFiles.length ? (
              <table className="td-table td-table--meeting">
                <thead><tr><th>Code</th><th>Meeting</th><th>Date</th></tr></thead>
                <tbody>
                  {meetingFiles.slice(0,5).map((f)=>(
                    <tr key={f.file_id||`${f.booking_id}-${f.meeting_file_code}`}>
                      <td><Badge bg={null} className="td-badge td-badge-green">{f.meeting_file_code||"-"}</Badge></td>
                      <td><span className="td-cm" title={f.meeting_name||"-"}>{f.meeting_name||"-"}</span></td>
                      <td>
                        <span className="td-cm">{formatDate(f.meeting_date)}</span>
                        <small>{f.meeting_time||"-"}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="td-empty td-empty--compact"><div className="td-ei"><i className="fa-solid fa-file-lines" aria-hidden="true"></i></div><p>No meeting files</p></div>
            )}
          </div>

          <div className="td-sgrid">
            <div><span>Recent Task</span><strong title={taskDisplayTitle(recentTasks[0])||"-"}>{truncate(taskDisplayTitle(recentTasks[0])||"-",22)}</strong></div>
            <div><span>Completion</span><strong>{completionRate}%</strong></div>
            <div><span>Range</span><strong>{range} Days</strong></div>
            <div><span>Statuses</span><strong>{statusRows.length}</strong></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnicianDashboard;
